import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Token helpers — single source of truth for auth
export const TOKEN_KEY = 'trabadju_access_token';
export const REFRESH_TOKEN_KEY = 'trabadju_refresh_token';
export const getAuthToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};
export const setAuthToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
};
export const getRefreshToken = () => {
  try { return localStorage.getItem(REFRESH_TOKEN_KEY); } catch { return null; }
};
export const setRefreshToken = (token) => {
  try {
    if (token) localStorage.setItem(REFRESH_TOKEN_KEY, token);
    else localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {}
};
export const clearAuth = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem('trabadju_session');
  } catch {}
};

const api = axios.create({
  baseURL: `${API_URL}/api`
});

// Attach Authorization header on every request when we have a token.
// This bypasses the cross-origin cookie issue (Cloudflare forces ACAO=* which
// is incompatible with `credentials: include`).
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// The access token only lives 15 minutes. On a 401, try ONE silent refresh
// (using the 7-day refresh token) and retry the original request before
// giving up — otherwise every session silently dies after 15 minutes of use
// (e.g. an employer reviewing candidates for a while, then a PDF/CV link
// that used to work suddenly 401s with no way to recover except re-login).
let refreshPromise = null;
const doRefresh = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('No refresh token');
  const { data } = await axios.post(`${API_URL}/api/auth/refresh`, { refresh_token: refreshToken });
  setAuthToken(data.access_token);
  setRefreshToken(data.refresh_token);
  return data.access_token;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;
    const status = error?.response?.status;
    const url = config?.url || '';
    const isAuthCall = url.includes('/auth/login') || url.includes('/auth/register') ||
      url.includes('/auth/refresh') || url.includes('/candidates/login') || url.includes('/candidates/register');
    // Session probes: AuthContext tries /auth/me then /candidates/me (or vice-versa).
    // A 401 on the "wrong" one is expected and must NOT wipe the session.
    const isProbe = url.endsWith('/auth/me') || url.endsWith('/candidates/me');
    if (status === 401 && isProbe) return Promise.reject(error);

    if (status === 401 && !isAuthCall && !config._retried && getRefreshToken()) {
      config._retried = true;
      try {
        refreshPromise = refreshPromise || doRefresh();
        const newToken = await refreshPromise;
        refreshPromise = null;
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${newToken}`;
        return api(config);
      } catch {
        refreshPromise = null;
        clearAuth();
        return Promise.reject(error);
      }
    }

    if (status === 401 && !isAuthCall) {
      clearAuth();
    }
    return Promise.reject(error);
  }
);

// Jobs API
export const jobsAPI = {
  create: (jobData) => api.post('/jobs', jobData),
  getAll: (params) => api.get('/jobs', { params }),
  getPublic: (params) => api.get('/jobs/public', { params }),
  getMyJobs: () => api.get('/jobs/my-jobs'),
  getById: (id) => api.get(`/jobs/${id}`),
  update: (id, jobData) => api.put(`/jobs/${id}`, jobData),
  delete: (id) => api.delete(`/jobs/${id}`),
  publish: (id) => api.post(`/jobs/${id}/publish`),
  close: (id, reason = 'filled') => api.post(`/jobs/${id}/close`, { reason }),
};

// Admin extras
export const adminExtrasAPI = {
  resetCandidates: () => api.post('/admin/candidates/reset', { confirmation: 'RESET CANDIDATOS' }),
};

// Sponsorships API (legacy — sponsorship retained for backward compat)
export const sponsorshipsAPI = {
  getPlans: () => api.get('/sponsorships/plans'),
  create: (data) => api.post('/sponsorships', data),
  getMySponsorships: () => api.get('/sponsorships/my-sponsorships')
};

// Employer profile API
export const employerAPI = {
  updateProfile: (data) => api.put('/employer/profile', data)
};

// Plans & Credits API (pay-per-post)
export const plansAPI = {
  getPlans: () => api.get('/plans'),
  getPaymentConfig: () => api.get('/payment-config'),
  getMyCredits: () => api.get('/employer/credits'),
  createPurchase: (data) => api.post('/purchases', data),
  quote: (data) => api.post('/purchases/quote', data),
  getLoyalty: () => api.get('/employer/loyalty'),
  getMyPurchases: () => api.get('/purchases'),
  getPurchase: (id) => api.get(`/purchases/${id}`),
  stripeCheckout: (id, originUrl) => api.post(`/purchases/${id}/stripe/checkout`, { origin_url: originUrl }),
  stripeStatus: (sessionId) => api.get(`/purchases/stripe/status/${sessionId}`),
  uploadProof: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/purchases/${id}/upload-proof`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Admin API
export const commerceAPI = {
  listPlans: () => api.get('/admin/commerce/plans'),
  createPlan: (data) => api.post('/admin/commerce/plans', data),
  updatePlan: (planType, data) => api.patch(`/admin/commerce/plans/${planType}`, data),
  deletePlan: (planType) => api.delete(`/admin/commerce/plans/${planType}`),
  listPromotions: () => api.get('/admin/commerce/promotions'),
  createPromotion: (data) => api.post('/admin/commerce/promotions', data),
  updatePromotion: (id, data) => api.patch(`/admin/commerce/promotions/${id}`, data),
  deletePromotion: (id) => api.delete(`/admin/commerce/promotions/${id}`),
  listRules: () => api.get('/admin/commerce/loyalty-rules'),
  createRule: (data) => api.post('/admin/commerce/loyalty-rules', data),
  updateRule: (id, data) => api.patch(`/admin/commerce/loyalty-rules/${id}`, data),
  deleteRule: (id) => api.delete(`/admin/commerce/loyalty-rules/${id}`),
  searchEmployers: (q) => api.get('/admin/commerce/employers', { params: { q } }),
  listGrants: () => api.get('/admin/commerce/grants'),
  createGrant: (data) => api.post('/admin/commerce/grants', data),
  stats: () => api.get('/admin/commerce/stats'),
  announcePromotion: (id, intro) => api.post(`/admin/commerce/promotions/${id}/announce`, { intro }),
};

export const adminAPI = {
  listPurchases: (status) => api.get('/admin/purchases', { params: status ? { status } : {} }),
  approvePurchase: (id, note) => api.post(`/admin/purchases/${id}/approve`, { note }),
  rejectPurchase: (id, note) => api.post(`/admin/purchases/${id}/reject`, { note }),
  getStats: () => api.get('/admin/stats'),
  // Job moderation
  listJobs: (params = {}) => api.get('/admin/jobs', { params }),
  deleteJob: (id, reason, notifyEmployer = true) =>
    api.delete(`/admin/jobs/${id}`, { data: { reason, notify_employer: notifyEmployer } }),
  // Registration analytics dashboard
  analyticsOverview: () => api.get('/admin/analytics/overview'),
  analyticsTimeseries: (days = 30) => api.get('/admin/analytics/timeseries', { params: { days } }),
  analyticsUsers: (params = {}) => api.get('/admin/analytics/users', { params }),
  analyticsUsersExportUrl: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    return `${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics/users/export${qs ? `?${qs}` : ''}`;
  },
  candidatesReportPdfUrl: () => `${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/candidates/pdf`,
  candidatesReportCsvUrl: () => `${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/candidates/csv`,
  candidatesReportSummary: () => api.get('/admin/reports/candidates/summary'),
  deleteUser: (role, id, reason) => api.delete(`/admin/users/${role}/${id}`, { data: { reason } }),
  verifyEmployer: (id, verified) => api.patch(`/admin/employers/${id}/verify`, { verified }),
  // Categories CRUD
  listCategories: () => api.get('/admin/categories'),
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (slug, data) => api.patch(`/admin/categories/${slug}`, data),
  deleteCategory: (slug) => api.delete(`/admin/categories/${slug}`),
  addSuggestion: (slug, data) => api.post(`/admin/categories/${slug}/suggestions`, data),
  updateSuggestion: (slug, index, data) => api.patch(`/admin/categories/${slug}/suggestions/${index}`, data),
  deleteSuggestion: (slug, index) => api.delete(`/admin/categories/${slug}/suggestions/${index}`),
};

// Analytics API
export const analyticsAPI = {
  trackView: (jobId, source = 'direct') => api.post('/analytics/track-view', { job_id: jobId, source }),
  getJobAnalytics: (jobId) => api.get(`/analytics/job/${jobId}`),
  getDashboard: () => api.get('/analytics/dashboard')
};

// Favourites API (candidate-only)
export const favouritesAPI = {
  list: () => api.get('/favourites'),
  add: (jobId) => api.post(`/favourites/${jobId}`),
  remove: (jobId) => api.delete(`/favourites/${jobId}`),
  check: (jobId) => api.get(`/favourites/check/${jobId}`),
};

// Public employer page (no auth)
export const publicAPI = {
  getEmployer: (slug) => api.get(`/public/employers/${slug}`),
  unsubscribeAlerts: (token) => api.post(`/alerts/unsubscribe/${token}`, null, { headers: { Accept: 'application/json' } }),
  alertsUnsubscribeStatus: (token) => api.get(`/alerts/unsubscribe/${token}`, { headers: { Accept: 'application/json' } }),
};

// Candidates API
export const candidatesAPI = {
  updateProfile: (profileData) => api.put('/candidates/profile', profileData),
  getMe: () => api.get('/candidates/me')
};

// Applications API
export const interviewsAPI = {
  propose: (payload) => api.post('/interviews', payload),
  listEmployer: () => api.get('/interviews/employer'),
  listCandidate: () => api.get('/interviews/candidate'),
  setOutcome: (id, outcome, note) => api.post(`/interviews/${id}/outcome`, { outcome, note }),
  cancel: (id) => api.post(`/interviews/${id}/cancel`),
  publicGet: (token) => api.get(`/public/interviews/${token}`),
  publicChoose: (token, slotIndex) => api.post(`/public/interviews/${token}/choose`, { slot_index: slotIndex }),
  publicDecline: (token, reason) => api.post(`/public/interviews/${token}/decline`, { reason })
};

export const applicationsAPI = {
  apply: (applicationData) => api.post('/applications/apply', applicationData),
  getMy: () => api.get('/applications/my-applications'),
  getMyApplications: () => api.get('/applications/my-applications'),
  getJobApplications: (jobId) => api.get(`/applications/job/${jobId}`),
  updateStatus: (applicationId, status) => api.put(`/applications/${applicationId}/status`, { status }),
  markViewed: (applicationId) => api.post(`/applications/${applicationId}/viewed`)
};

// Upload API
export const uploadAPI = {
  uploadCV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/cv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadCoverLetter: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/cover-letter', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadIDCard: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/id-card', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadRecognitionCert: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/recognition-certificate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadCertificate: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/certificate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadDocument: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Candidate Showcase API — "Talento em Cabo Verde"
export const showcaseAPI = {
  list: (params) => api.get('/showcase/candidates', { params }),
  get: (id) => api.get(`/showcase/candidates/${id}`),
  unlock: (id) => api.post(`/showcase/candidates/${id}/unlock`),
  message: (id, payload) => api.post(`/showcase/candidates/${id}/message`, payload),
  inbox: () => api.get('/showcase/messages'),
  updateSelf: (payload) => api.patch('/showcase/me', payload),
  adminUpdate: (id, payload) => api.patch(`/admin/showcase/candidates/${id}`, payload),
  stats: () => api.get('/showcase/stats')
};

export default api;
